<template>
  <div>
    <CCard>
      <CCardHeader>
        <strong>Complete Document Package</strong>
        <div class="float-right">
          <CButton
            color="primary"
            size="sm"
            @click="downloadAllPdf"
            :disabled="isDownloadingAll"
            class="mr-2"
          >
            <CSpinner v-if="isDownloadingAll" size="sm" class="mr-1" />
            <CIcon v-else name="cil-cloud-download" />
            {{ isDownloadingAll ? downloadProgress : 'Download All as PDF' }}
          </CButton>
        </div>
      </CCardHeader>
      <CCardBody>
        <CDataTable
          :items="documentSequence"
          :fields="fields"
          hover
          striped
          :items-per-page="20"
        >
          <template #sequence="{ item }">
            <td>{{ item.sequence }}</td>
          </template>
          <template #name="{ item }">
            <td>
              <strong>{{ item.name }}</strong>
              <CBadge :color="item.status === 'Available' ? 'success' : 'secondary'" class="ml-2">
                {{ item.status }}
              </CBadge>
            </td>
          </template>
          <template #actions="{ item }">
            <td>
              <CButton
                v-if="item.status === 'Available'"
                color="info"
                size="sm"
                @click="previewDocument(item)"
                class="mr-1"
              >
                <CIcon name="cil-eye" /> Preview
              </CButton>
              <CButton
                v-if="item.status === 'Available'"
                color="primary"
                size="sm"
                @click="downloadDocument(item)"
              >
                <CIcon name="cil-download" /> Download PDF
              </CButton>
            </td>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>

    <!-- Preview Modal -->
    <CModal size="xl" :show.sync="previewModal" :closeOnBackdrop="false">
      <template #header>
        <h5>{{ currentDocument.name }}</h5>
        <CButton
          @click="previewModal = false"
          class="close"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </CButton>
      </template>
      <div v-if="currentDocument.type === 'generated'" class="preview-content preview-pdf-mode">
        <component
          ref="previewComponent"
          :is="currentDocument.component"
          :company="company"
          :bank="bank"
          :company-bank-account="companyBankAccount"
          :stock="stock"
          :hide-controls="true"
          v-if="currentDocument.component"
        />
      </div>
      <div v-else-if="currentDocument.type === 'uploaded'" class="preview-content">
        <iframe
          :src="currentDocument.url"
          width="100%"
          height="600px"
          style="border: 1px solid #ccc;"
        ></iframe>
      </div>
      <template #footer>
        <CButton color="secondary" @click="previewModal = false">Close</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import html2pdf from "html2pdf.js";
import { PDFDocument } from "pdf-lib";
import WidgetsReportDocumentSubmission from "./WidgetsReportDocumentSubmission";
import WidgetsReportFullInvoice from "./WidgetsReportFullInvoice";
import WidgetsReportEoeInvoice from "./WidgetsReportEoeInvoice";
import WidgetsReportAccountNotice from "./WidgetsReportAccountNotice";
import WidgetsReportDeliveryOrder from "./WidgetsReportDeliveryOrder";
import WidgetsReportEHakmilik from "./WidgetsReportEHakmilik";

export default {
  name: "WidgetsDisbursementMasterView",
  components: {
    WidgetsReportDocumentSubmission,
    WidgetsReportFullInvoice,
    WidgetsReportEoeInvoice,
    WidgetsReportAccountNotice,
    WidgetsReportDeliveryOrder,
    WidgetsReportEHakmilik,
  },
  props: {
    stock: {
      type: Object,
      required: true
    },
    company: {
      type: Object,
      required: true
    },
    bank: {
      type: Object,
      required: true
    },
    companyBankAccount: {
      type: Object,
      required: true
    },
  },
  data() {
    return {
      previewModal: false,
      currentDocument: {},
      isDownloadingAll: false,
      downloadProgress: "",
      fields: [
        { key: "sequence", label: "#", _style: "width: 5%;" },
        { key: "name", label: "Document Name", _style: "width: 60%;" },
        { key: "actions", label: "Actions", _style: "width: 35%;" },
      ],
    };
  },
  computed: {
    documentSequence() {
      const apiUrl = process.env.VUE_APP_API_URL || window.config?.API_URL || '';

      return [
        {
          sequence: 1,
          name: "DISBURSE LETTER",
          type: "generated",
          component: "WidgetsReportDocumentSubmission",
          status: "Available",
        },
        {
          sequence: 2,
          name: "CUSTOMER INVOICE",
          type: "generated",
          component: "WidgetsReportFullInvoice",
          status: "Available",
        },
        {
          sequence: 3,
          name: "INVOICE EOE",
          type: "generated",
          component: "WidgetsReportEoeInvoice",
          status: "Available",
        },
        {
          sequence: 4,
          name: "ACCOUNT NOTICE",
          type: "generated",
          component: "WidgetsReportAccountNotice",
          status: "Available",
        },
        {
          sequence: 5,
          name: "GERAN (Vehicle Registration Cert.)",
          type: "uploaded",
          url: this.getDocumentUrl("jpjGeran"),
          status: this.getDocumentStatus("jpjGeran"),
        },
        {
          sequence: 6,
          name: "INSURANS (Insurance)",
          type: "uploaded",
          url: this.getDocumentUrl("insurance"),
          status: this.getDocumentStatus("insurance"),
        },
        {
          sequence: 7,
          name: "ROADTAX",
          type: "uploaded",
          url: this.getDocumentUrl("roadTax"),
          status: this.getDocumentStatus("roadTax"),
        },
        {
          sequence: 8,
          name: "EDAFTAR (JPJ E-Daftar)",
          type: "uploaded",
          url: this.getDocumentUrl("jpjEDaftar"),
          status: this.getDocumentStatus("jpjEDaftar"),
        },
        {
          sequence: 9,
          name: "RESIT EDAFTAR (E-Daftar Receipt)",
          type: "uploaded",
          url: this.getDocumentUrl("receiptEDaftar"),
          status: this.getDocumentStatus("receiptEDaftar"),
        },
        {
          sequence: 10,
          name: "EHAK (JPJ E-Hakmilik)",
          type: "uploaded",
          url: this.getDocumentUrl("jpjEHakmilik"),
          status: this.getDocumentStatus("jpjEHakmilik"),
        },
        {
          sequence: 11,
          name: "RESIT KASTAM (Customs Receipt)",
          type: "uploaded",
          url: this.getDocumentUrl("receiptKastam"),
          status: this.getDocumentStatus("receiptKastam"),
        },
        {
          sequence: 12,
          name: "K1 Documents",
          type: "uploaded",
          url: this.getDocumentUrl("k1"),
          status: this.getDocumentStatus("k1"),
        },
        {
          sequence: 13,
          name: "K8/JK69 Documents",
          type: "uploaded",
          url: this.getDocumentUrl("k8"),
          status: this.getDocumentStatus("k8"),
        },
        {
          sequence: 14,
          name: "SIJIL EXPORT (Export Certificate)",
          type: "uploaded",
          url: this.getDocumentUrl("exportCert"),
          status: this.getDocumentStatus("exportCert"),
        },
        {
          sequence: 15,
          name: "B2 (Puspakom B2 Slip)",
          type: "uploaded",
          url: this.getDocumentUrl("puspakomB2"),
          status: this.getDocumentStatus("puspakomB2"),
        },
        {
          sequence: 16,
          name: "B7 (Puspakom B7 Slip)",
          type: "uploaded",
          url: this.getDocumentUrl("puspakomB7"),
          status: this.getDocumentStatus("puspakomB7"),
        },
        {
          sequence: 17,
          name: "IC CUSTOMER",
          type: "uploaded",
          url: this.getDocumentUrl("customerIc"),
          status: this.getDocumentStatus("customerIc"),
        },
        {
          sequence: 18,
          name: "DELIVERY ORDER",
          type: "generated",
          component: "WidgetsReportDeliveryOrder",
          status: "Available",
        },
        {
          sequence: 19,
          name: "E-HAKMILIK LETTER",
          type: "generated",
          component: "WidgetsReportEHakmilik",
          status: "Available",
        },
      ];
    },
  },
  methods: {
    getDocumentUrl(type) {
      const apiUrl = process.env.VUE_APP_API_URL || window.config?.API_URL || '';

      const mapping = {
        jpjGeran: this.stock.registration?.jpjGeranDocuments,
        insurance: this.stock.registration?.insuranceDocuments,
        roadTax: this.stock.registration?.roadTaxDocuments,
        jpjEDaftar: this.stock.registration?.jpjEDaftarDocuments,
        receiptEDaftar: this.stock.registration?.receiptEDaftarDocuments,
        jpjEHakmilik: this.stock.registration?.jpjEHakMilikDocuments,
        receiptKastam: this.stock.registration?.receiptKastamDocuments,
        k1: this.stock.clearance?.k1Documents,
        k8: this.stock.clearance?.k8Documents,
        exportCert: this.stock.clearance?.exportCertificateDocuments,
        puspakomB2: this.stock.registration?.puspakomB2SlipDocuments,
        puspakomB7: this.stock.registration?.puspakomB7SlipDocuments,
        customerIc: this.stock.sale?.customerIcDocuments,
      };

      const docs = mapping[type];
      if (docs && docs.length > 0) {
        return `${apiUrl}Documents/File/${docs[0].documentId}`;
      }
      return null;
    },
    getDocumentStatus(type) {
      return this.getDocumentUrl(type) ? "Available" : "Not Uploaded";
    },
    previewDocument(item) {
      this.currentDocument = item;
      this.previewModal = true;
    },
    async downloadDocument(item) {
      if (item.type === "uploaded" && item.url) {
        // For uploaded documents, open in new tab for download
        window.open(item.url, "_blank");
      } else if (item.type === "generated" && item.component) {
        // For generated documents, convert to PDF
        await this.downloadGeneratedPdf(item);
      }
    },
    // The company logo is a cross-origin image (served from the API origin).
    // html2canvas drops cross-origin images from the rendered PDF (tainted
    // canvas), so the logo would be missing. Fetch each logo and swap its src
    // for a same-origin base64 data URI before generating the PDF.
    async inlineLogoImages(rootEl) {
      if (!rootEl) return;
      const imgs = Array.from(rootEl.querySelectorAll("img.logo-image"));
      await Promise.all(
        imgs.map(async (img) => {
          try {
            const src = img.getAttribute("src");
            if (!src || src.startsWith("data:")) return;
            // Cache-buster + no-store: the logo is served cross-origin (API
            // origin, behind Cloudflare). A prior plain <img> load caches a
            // NO-CORS response without Access-Control-Allow-Origin; a CORS
            // fetch then reuses that cached entry and is blocked. A unique
            // URL forces a fresh CORS request that returns the ACAO header.
            const bust = src + (src.includes("?") ? "&" : "?") + "cb=" + Date.now();
            const resp = await fetch(bust, { mode: "cors", cache: "no-store" });
            if (!resp.ok) return;
            const blob = await resp.blob();
            const dataUri = await new Promise((res, rej) => {
              const fr = new FileReader();
              fr.onload = () => res(fr.result);
              fr.onerror = rej;
              fr.readAsDataURL(blob);
            });
            img.src = dataUri;
            if (img.decode) {
              try { await img.decode(); } catch (e) { /* ignore decode hiccups */ }
            }
          } catch (e) {
            console.warn("Logo inline failed; PDF may omit the logo:", e);
          }
        })
      );
    },
    async downloadGeneratedPdf(item) {
      // Create a temporary container
      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.left = "-9999px";
      tempContainer.style.top = "0";
      document.body.appendChild(tempContainer);

      // Create Vue instance for the component
      const Vue = this.$root.constructor;
      const ComponentClass = Vue.extend(this.$options.components[item.component]);
      const instance = new ComponentClass({
        propsData: {
          stock: this.stock,
          company: this.company,
          bank: this.bank,
          companyBankAccount: this.companyBankAccount,
        },
      });

      // Mount and render
      instance.$mount();
      tempContainer.appendChild(instance.$el);

      // Wait a bit for rendering
      await this.$nextTick();

      // Add pdf-generation class to element before PDF generation
      instance.$el.classList.add('pdf-generation');

      // Apply inline styling for better PDF rendering
      instance.$el.style.width = '794px';
      instance.$el.style.backgroundColor = '#ffffff';

      // Inline the cross-origin logo so html2canvas includes it in the PDF.
      await this.inlineLogoImages(instance.$el);

      // PDF options with professional 20mm margins on all sides
      const opt = {
        margin: [20, 20, 20, 20], // [top, right, bottom, left] in mm - professional margins
        filename: `${item.name}_${this.stock.stockNo}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: 0
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ['css', 'legacy'] }
      };

      try {
        // Generate PDF
        await html2pdf().set(opt).from(instance.$el).save();
      } catch (error) {
        console.error("PDF generation error:", error);
        alert("Error generating PDF. Please try again.");
      } finally {
        // Cleanup
        instance.$destroy();
        document.body.removeChild(tempContainer);
      }
    },
    printCurrentDocument() {
      window.print();
    },
    async downloadAllPdf() {
      this.isDownloadingAll = true;
      this.downloadProgress = "Starting...";

      try {
        // Create a new merged PDF document
        const mergedPdf = await PDFDocument.create();
        const availableDocs = this.documentSequence.filter(doc => doc.status === 'Available');
        let currentIndex = 0;

        for (const doc of availableDocs) {
          currentIndex++;
          this.downloadProgress = `Processing ${currentIndex} of ${availableDocs.length}...`;

          try {
            let pdfBytes = null;

            if (doc.type === 'generated') {
              // Generate PDF for generated documents
              pdfBytes = await this.generatePdfBytes(doc);
            } else if (doc.type === 'uploaded' && doc.url) {
              // Fetch uploaded PDF
              pdfBytes = await this.fetchUploadedPdf(doc.url);
            }

            if (pdfBytes) {
              // Load the PDF and copy its pages to merged PDF
              const pdfDoc = await PDFDocument.load(pdfBytes);
              const copiedPages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
              copiedPages.forEach((page) => mergedPdf.addPage(page));
            }
          } catch (error) {
            console.error(`Error processing ${doc.name}:`, error);
            // Continue with other documents even if one fails
          }
        }

        this.downloadProgress = "Finalizing PDF...";

        // Save the merged PDF
        const mergedPdfBytes = await mergedPdf.save();
        const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Disbursement_Package_${this.stock.stockNo}.pdf`;
        link.click();
        URL.revokeObjectURL(url);

        this.downloadProgress = "Complete!";
        setTimeout(() => {
          this.isDownloadingAll = false;
          this.downloadProgress = "";
        }, 1000);

      } catch (error) {
        console.error("Error creating combined PDF:", error);
        alert("Error creating combined PDF. Please try individual downloads.");
        this.isDownloadingAll = false;
        this.downloadProgress = "";
      }
    },
    async generatePdfBytes(doc) {
      return new Promise(async (resolve, reject) => {
        try {
          // Create temporary container
          const tempContainer = document.createElement("div");
          tempContainer.style.position = "absolute";
          tempContainer.style.left = "-9999px";
          tempContainer.style.top = "0";
          document.body.appendChild(tempContainer);

          // Create Vue instance for the component
          const Vue = this.$root.constructor;
          const ComponentClass = Vue.extend(this.$options.components[doc.component]);
          const instance = new ComponentClass({
            propsData: {
              stock: this.stock,
              company: this.company,
              bank: this.bank,
              companyBankAccount: this.companyBankAccount,
            },
          });

          // Mount and render
          instance.$mount();
          tempContainer.appendChild(instance.$el);
          await this.$nextTick();

          // Add pdf-generation class and inline styling for better PDF rendering
          instance.$el.classList.add('pdf-generation');
          instance.$el.style.width = '794px';
          instance.$el.style.backgroundColor = '#ffffff';

          // Inline the cross-origin logo so html2canvas includes it in the PDF.
          await this.inlineLogoImages(instance.$el);

          // PDF options with professional 20mm margins on all sides
          const opt = {
            margin: [20, 20, 20, 20], // [top, right, bottom, left] in mm - professional margins
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              backgroundColor: '#ffffff',
              scrollX: 0,
              scrollY: 0
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            pagebreak: { mode: ['css', 'legacy'] }
          };

          // Generate PDF as blob
          const pdfBlob = await html2pdf().set(opt).from(instance.$el).output('blob');
          const arrayBuffer = await pdfBlob.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);

          // Cleanup
          instance.$destroy();
          document.body.removeChild(tempContainer);

          resolve(uint8Array);
        } catch (error) {
          reject(error);
        }
      });
    },
    async fetchUploadedPdf(url) {
      try {
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        return new Uint8Array(arrayBuffer);
      } catch (error) {
        console.error("Error fetching uploaded PDF:", error);
        return null;
      }
    },
  },
};
</script>

<style scoped>
.preview-content {
  max-height: 70vh;
  overflow-y: auto;
}

/* Make preview look like PDF output */
.preview-pdf-mode {
  background-color: #f5f5f5;
  padding: 20px;
}

.preview-pdf-mode >>> .pdf-content {
  width: 794px !important;
  max-width: 100%;
  margin: 0 auto;
  padding: 0 !important;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.preview-pdf-mode >>> .card {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

.preview-pdf-mode >>> .card-body {
  padding: 20mm !important;
}

.preview-pdf-mode >>> .no-print {
  display: none !important;
}

.preview-pdf-mode >>> .logo-image {
  max-width: 100px !important;
}

.preview-pdf-mode >>> .company-name {
  font-size: 18px !important;
}
</style>
